import { ApolloClient, gql, HttpLink, InMemoryCache, split } from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'
import { WebSocketLink } from '@apollo/client/link/ws'

export const FIRST_VIDEO_QUERY = gql`
  query FirstVideoQuery {
    videoLeaderboard(
      orderBy: { sevenDays: DESC }
      offset: 0
      limit: 1
      where: { mintObject: { id: { _isNull: false } } }
    ) {
      mintObject {
        dropTime {
          dropTime
        }
        id
        supply
        floorPriceObject {
          floor
        }
        milestones {
          endTime
          nextMilestone
        }
        editionMetadata {
          maxSupply
          primarySalePrice
          metadataObject {
            purchaseReceiptsAggregate {
              aggregate {
                sum {
                  price
                }
              }
            }
          }
        }
      }
    }
  }
`

export const HOME_QUERY = gql`
  query HomeQuery($offset: Int, $limit: Int) {
    mint(
      offset: $offset
      limit: $limit
      orderBy: { createdAt: DESC }
      where: { editionMetadata: { id: { _isNull: false } } }
    ) {
      dropTime {
        dropTime
      }
      supply
      id
      floorPriceObject {
        floor
      }
      milestones {
        endTime
        nextMilestone
      }
      editionMetadata {
        maxSupply
        primarySalePrice
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
        }
      }
    }
  }
`

export const UPCOMING_QUERY = gql`
  query UpcomingQuery($offset: Int, $limit: Int, $now: timestamptz, $end: timestamptz) {
    mint(
      offset: $offset
      limit: $limit
      orderBy: { dropTime: { dropTime: ASC } }
      where: {
        editionMetadata: { id: { _isNull: false } }
        dropTime: { dropTime: { _gt: $now, _lt: $end } }
      }
    ) {
      dropTime {
        dropTime
      }
      supply
      id
      floorPriceObject {
        floor
      }
      milestones {
        endTime
        nextMilestone
      }
      editionMetadata {
        maxSupply
        primarySalePrice
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
        }
      }
    }
  }
`

export const LATEST_QUERY = gql`
  query LatestQuery($offset: Int, $limit: Int, $now: timestamptz) {
    mint(
      offset: $offset
      limit: $limit
      orderBy: { dropTime: { dropTime: DESC_NULLS_LAST }, createdAt: DESC }
      where: { editionMetadata: { id: { _isNull: false } }, dropTime: { dropTime: { _lte: $now } } }
    ) {
      dropTime {
        dropTime
      }
      supply
      id
      floorPriceObject {
        floor
      }
      milestones {
        endTime
        nextMilestone
      }
      editionMetadata {
        maxSupply
        primarySalePrice
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
        }
      }
    }
  }
`

export const MORE_VIDEOS_QUERY = gql`
  query MoreVideosFeed($mint: String!, $offset: Int, $limit: Int, $end: timestamptz) {
    mint(where: { id: { _eq: $mint } }) {
      mintRelations(
        offset: $offset
        limit: $limit
        orderBy: { amountSum: DESC }
        where: {
          relatedMintObject: { id: { _isNull: false }, dropTime: { dropTime: { _lt: $end } } }
        }
      ) {
        relatedMintObject {
          dropTime {
            dropTime
          }
          milestones {
            endTime
            nextMilestone
          }
          supply
          id
          floorPriceObject {
            floor
          }
          editionMetadata {
            maxSupply
            primarySalePrice
            metadataObject {
              purchaseReceiptsAggregate {
                aggregate {
                  sum {
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const CREATED_VIDEOS_QUERY = gql`
  query CreatedVideos($address: String!, $offset: Int, $limit: Int, $end: timestamptz) {
    editionMetadata(
      offset: $offset
      limit: $limit
      where: {
        tokenOwner: { _eq: $address }
        mintObject: { dropTime: { dropTime: { _lt: $end } } }
      }
      orderBy: { createdAt: DESC }
    ) {
      maxSupply
      primarySalePrice
      mintObject {
        dropTime {
          dropTime
        }
        id
        supply
        floorPriceObject {
          floor
        }
        milestones {
          endTime
          nextMilestone
        }
      }
      metadataObject {
        purchaseReceiptsAggregate {
          aggregate {
            sum {
              price
            }
          }
        }
      }
    }
  }
`

export const COLLECTED_VIDEOS_QUERY = gql`
  query CollectedVideos($address: String!, $offset: Int, $limit: Int) {
    tokenAccount(
      offset: $offset
      limit: $limit
      where: { owner: { _eq: $address }, amount: { _gt: 0 } }
      orderBy: { createdAt: DESC }
    ) {
      mintObject {
        dropTime {
          dropTime
        }
        id
        supply
        floorPriceObject {
          floor
        }
        milestones {
          endTime
          nextMilestone
        }
        editionMetadata {
          maxSupply
          primarySalePrice
          metadataObject {
            purchaseReceiptsAggregate {
              aggregate {
                sum {
                  price
                }
              }
            }
          }
        }
      }
    }
  }
`

export const LIKED_VIDEOS_QUERY = gql`
  query HomePage($mints: [String!]!, $end: timestamptz) {
    mint(where: { id: { _in: $mints }, dropTime: { dropTime: { _lt: $end } } }) {
      dropTime {
        dropTime
      }
      supply
      id
      floorPriceObject {
        floor
      }
      milestones {
        endTime
        nextMilestone
      }
      editionMetadata {
        maxSupply
        primarySalePrice
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
        }
      }
    }
  }
`

export const PRIMARY_SUBSCRIPTION = gql`
  subscription GetPrimary($mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      dropTime {
        dropTime
      }
      supply
      editionMetadata {
        maxSupply
        primarySalePrice
      }
      milestones {
        endTime
        nextMilestone
      }
    }
  }
`

export const SECONDARY_SUBSCRIPTION = gql`
  subscription GetSecondary($mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      floorPriceObject {
        floor
      }
      listingsWithTokenAggregate {
        aggregate {
          count
          min {
            price
          }
        }
      }
      editionMetadata {
        maxSupply
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
          bidReceiptsAggregate(
            where: {
              _and: { purchaseReceipt: { _isNull: true }, canceledAtOnChain: { _isNull: true } }
            }
          ) {
            aggregate {
              max {
                price
              }
            }
          }
        }
      }
      tokenAccountsAggregate(where: { amount: { _gt: "0" } }) {
        aggregate {
          count
        }
      }
      supply
    }
  }
`

export const SECONDARY_USER_SUBSCRIPTION = gql`
  subscription GetUserSecondary($mint: String!, $user: String!) {
    mint(where: { id: { _eq: $mint } }) {
      tokenAccountsAggregate(where: { owner: { _eq: $user } }) {
        aggregate {
          sum {
            amount
          }
        }
      }
      listingsWithTokenAggregate(where: { seller: { _neq: $user } }) {
        aggregate {
          min {
            price
          }
        }
      }
      editionMetadata {
        creatorFeeBasisPoints
        platformFeeBasisPoints
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              sum {
                price
              }
            }
          }
          bidReceiptsAggregate(
            where: {
              _and: {
                purchaseReceipt: { _isNull: true }
                canceledAtOnChain: { _isNull: true }
                buyer: { _neq: $user }
              }
            }
          ) {
            aggregate {
              max {
                price
              }
            }
          }
        }
      }
    }
  }
`

export const BALANCE_SUBSCRIPTION = gql`
  subscription GetBalance($mint: String!, $user: String!) {
    mint(where: { id: { _eq: $mint } }) {
      tokenAccountsAggregate(where: { owner: { _eq: $user } }) {
        aggregate {
          sum {
            amount
          }
        }
      }
    }
  }
`

export const LISTINGS_QUERY = gql`
  query GetListings($offset: Int, $limit: Int, $mint: String!) {
    listings(
      where: {
        mint: { _eq: $mint }
        purchaseReceipt: { _isNull: true }
        canceledAtOnChain: { _isNull: true }
        tokenAccountObject: { delegatedAmount: { _gt: "0" }, amount: { _gt: "0" } }
      }
      limit: $limit
      offset: $offset
      orderBy: { price: ASC }
    ) {
      id
      price
      seller
      createdAtOnChain
      canceledAtOnChain
    }
  }
`

export const LISTINGS_ADD_SUBSCRIPTION = gql`
  subscription SubscribeListingsAdd($offset: Int, $limit: Int, $mint: String!) {
    listings(
      where: {
        mint: { _eq: $mint }
        purchaseReceipt: { _isNull: true }
        canceledAtOnChain: { _isNull: true }
        tokenAccountObject: { delegatedAmount: { _gt: "0" }, amount: { _gt: "0" } }
      }
      limit: $limit
      offset: $offset
      orderBy: { createdAtOnChain: DESC }
    ) {
      price
      seller
      createdAtOnChain
      canceledAtOnChain
    }
  }
`

export const LISTINGS_BUY_SUBSCRIPTION = gql`
  subscription SubscribeListingsBuy($offset: Int, $limit: Int, $mint: String!) {
    listings(
      where: { mint: { _eq: $mint }, purchaseReceipt: { _isNull: false } }
      limit: $limit
      offset: $offset
      orderBy: { modifiedAt: DESC }
    ) {
      price
      seller
      createdAtOnChain
      modifiedAt
    }
  }
`

export const LISTINGS_CANCEL_SUBSCRIPTION = gql`
  subscription SubscribeListingsCancel($offset: Int, $limit: Int, $mint: String!) {
    listings(
      where: { mint: { _eq: $mint }, canceledAtOnChain: { _isNull: false } }
      limit: $limit
      offset: $offset
      orderBy: { canceledAtOnChain: DESC }
    ) {
      price
      seller
      createdAtOnChain
      canceledAtOnChain
    }
  }
`

export const OFFERS_QUERY = gql`
  query GetOffers($offset: Int, $limit: Int, $mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      editionMetadata {
        metadataObject {
          bidReceipts(
            offset: $offset
            limit: $limit
            orderBy: { price: DESC }
            where: {
              _and: { canceledAtOnChain: { _isNull: true }, purchaseReceipt: { _isNull: true } }
            }
          ) {
            createdAtOnChain
            price
            buyer
          }
        }
      }
    }
  }
`

export const OFFERS_ADD_SUBSCRIPTION = gql`
  subscription SubscribeOffersAdd($offset: Int, $limit: Int, $mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      editionMetadata {
        metadataObject {
          bidReceipts(
            offset: $offset
            limit: $limit
            orderBy: { createdAtOnChain: DESC }
            where: {
              _and: { canceledAtOnChain: { _isNull: true }, purchaseReceipt: { _isNull: true } }
            }
          ) {
            createdAtOnChain
            price
            buyer
          }
        }
      }
    }
  }
`

export const OFFERS_ACCEPT_SUBSCRIPTION = gql`
  subscription SubscribeOffersAccept($offset: Int, $limit: Int, $mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      editionMetadata {
        metadataObject {
          bidReceipts(
            offset: $offset
            limit: $limit
            orderBy: { modifiedAt: DESC }
            where: { purchaseReceipt: { _isNull: false } }
          ) {
            createdAtOnChain
            price
            buyer
            modifiedAt
          }
        }
      }
    }
  }
`

export const OFFERS_CANCEL_SUBSCRIPTION = gql`
  subscription SubscribeOffersCancel($offset: Int, $limit: Int, $mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      editionMetadata {
        metadataObject {
          bidReceipts(
            offset: $offset
            limit: $limit
            orderBy: { canceledAtOnChain: DESC }
            where: { canceledAtOnChain: { _isNull: false } }
          ) {
            canceledAtOnChain
            createdAtOnChain
            price
            buyer
          }
        }
      }
    }
  }
`

export const OFFERS_RECEIVED_SUBSCRIPTION = gql`
  subscription NumOffersReceived($user: String!) {
    bidReceiptAggregate(
      orderBy: { createdAtOnChain: DESC }
      where: {
        _and: {
          canceledAtOnChain: { _isNull: true }
          purchaseReceipt: { _isNull: true }
          metadataObject: {
            mintObject: { tokenAccounts: { _and: { amount: { _gt: "0" }, owner: { _eq: $user } } } }
          }
          buyer: { _neq: $user }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const OFFERS_RECEIVED_QUERY = gql`
  query OffersReceived($user: String!, $viewer: String!, $limit: Int, $offset: Int) {
    bidReceipt(
      offset: $offset
      limit: $limit
      orderBy: { createdAtOnChain: DESC }
      where: {
        _and: {
          canceledAtOnChain: { _isNull: true }
          purchaseReceipt: { _isNull: true }
          metadataObject: {
            mintObject: { tokenAccounts: { _and: { amount: { _gt: "0" }, owner: { _eq: $user } } } }
          }
          buyer: { _neq: $user }
        }
      }
    ) {
      createdAtOnChain
      price
      buyer
      metadataObject {
        mint
        mintObject {
          tokenAccounts(where: { owner: { _eq: $viewer }, amount: { _gt: "0" } }) {
            amount
          }
        }
      }
    }
  }
`

export const OFFERS_MADE_SUBSCRIPTION = gql`
  subscription NumOffersMade($user: String!) {
    bidReceiptAggregate(
      orderBy: { createdAtOnChain: DESC }
      where: {
        _and: {
          canceledAtOnChain: { _isNull: true }
          purchaseReceipt: { _isNull: true }
          buyer: { _eq: $user }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const OFFERS_MADE_QUERY = gql`
  query OffersMade($user: String!, $viewer: String!, $limit: Int, $offset: Int) {
    bidReceipt(
      offset: $offset
      limit: $limit
      orderBy: { createdAtOnChain: DESC }
      where: {
        _and: {
          canceledAtOnChain: { _isNull: true }
          purchaseReceipt: { _isNull: true }
          buyer: { _eq: $user }
        }
      }
    ) {
      createdAtOnChain
      price
      buyer
      metadataObject {
        mint
        mintObject {
          tokenAccounts(
            where: {
              owner: { _eq: $viewer }
              amount: { _gt: "0" }
              _and: { owner: { _neq: $user } }
            }
          ) {
            amount
          }
        }
      }
    }
  }
`

export const RECEIPTS_QUERY = gql`
  query GetReceipts($offset: Int, $limit: Int, $mint: String!) {
    receipt(
      offset: $offset
      limit: $limit
      where: { mint: { _eq: $mint } }
      orderBy: { createdAt: DESC }
    ) {
      receiptType
      buyer
      seller
      price
      createdAt
      tokenSize
    }
  }
`

export const COLLECTORS_QUERY = gql`
  query GetCollectors($offset: Int, $limit: Int, $mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      tokenAccounts(
        offset: $offset
        limit: $limit
        where: { amount: { _gt: "0" } }
        orderBy: { amount: DESC }
      ) {
        amount
        createdAt
        owner
      }
    }
  }
`

export const COLLECTORS_SUBSCRIPTION = gql`
  subscription GetCollectors($mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      tokenAccounts(where: { amount: { _gt: "0" } }, orderBy: { amount: DESC }) {
        amount
        createdAt
        owner
      }
    }
  }
`

export const PURCHASES_SUBSCRIPTION = gql`
  subscription GetPurchasesCount($mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      primaryReceiptsAggregate {
        aggregate {
          count
        }
      }
      editionMetadata {
        metadataObject {
          purchaseReceiptsAggregate {
            aggregate {
              count
            }
          }
        }
      }
    }
  }
`

export const USER_ACTIVITY_QUERY = gql`
  query UserActivity($offset: Int, $limit: Int, $address: String!) {
    receipt(
      offset: $offset
      limit: $limit
      orderBy: { createdAt: DESC }
      where: { _or: [{ seller: { _eq: $address } }, { buyer: { _eq: $address } }] }
    ) {
      buyer
      createdAt
      receiptType
      price
      mint
      seller
      tokenSize
    }
  }
`

export const NUM_LISTINGS_SUBSCRIPTION = gql`
  subscription NumListings($address: String!) {
    listingsAggregate(
      where: {
        seller: { _eq: $address }
        tokenAccountObject: { delegatedAmount: { _gt: "0" }, amount: { _gt: "0" } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const USER_LISTINGS_QUERY = gql`
  query UserListings($offset: Int, $limit: Int, $address: String!) {
    listings(
      offset: $offset
      limit: $limit
      orderBy: { createdAtOnChain: DESC }
      where: {
        seller: { _eq: $address }
        tokenAccountObject: { delegatedAmount: { _gt: "0" }, amount: { _gt: "0" } }
      }
    ) {
      seller
      createdAtOnChain
      price
      metadataObject {
        mint
      }
    }
  }
`

export const OFFER_STATUS_QUERY = gql`
  query CheckOfferStatus($address: String!, $mint: String!) {
    bidReceiptAggregate(
      where: {
        buyer: { _eq: $address }
        metadataObject: { mint: { _eq: $mint } }
        purchaseReceipt: { _isNull: true }
        canceledAtOnChain: { _isNull: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`

export const LISTING_STATUS_QUERY = gql`
  query CheckListingStatus($address: String!, $mint: String!) {
    tokenAccount(where: { mint: { _eq: $mint }, owner: { _eq: $address } }) {
      mintObject {
        editionMetadata {
          metadataObject {
            listingReceipts(
              where: {
                seller: { _eq: $address }
                canceledAtOnChain: { _isNull: true }
                purchaseReceipt: { _isNull: true }
              }
              orderBy: { price: ASC }
              limit: 1
            ) {
              price
            }
            listingReceiptsAggregate(
              where: {
                seller: { _eq: $address }
                canceledAtOnChain: { _isNull: true }
                purchaseReceipt: { _isNull: true }
              }
            ) {
              aggregate {
                count
              }
            }
          }
        }
      }
      amount
      delegatedAmount
      delegate
    }
  }
`

export const PUBLIC_METRICS_QUERY = gql`
  query PublicMetrics($from: bigint) {
    primaryReceiptAggregate {
      aggregate {
        sum {
          total_primary_volume: price
        }
        number_of_primary_sales: count
      }
    }
    purchaseReceiptAggregate {
      aggregate {
        sum {
          total_secondary_volume: price
        }
        number_of_secondary_sales: count
      }
    }
    tokenAccountAggregate {
      aggregate {
        unique_collectors: count(distinct: true, columns: owner)
      }
    }
    mintAggregate {
      aggregate {
        number_of_videos: count(distinct: true, columns: id)
      }
    }
    creatorAggregate {
      aggregate {
        unique_creators: count(columns: address, distinct: true)
      }
    }
    primaryReceiptAggregate24Hours: primaryReceiptAggregate(
      where: { createdAtOnChain: { _gte: $from } }
    ) {
      aggregate {
        sum {
          primary_volume_24hrs: price
        }
        number_of_primary_sales_24hrs: count
      }
    }
    purchaseReceiptAggregate24Hours: purchaseReceiptAggregate(
      where: { createdAtOnChain: { _gte: $from } }
    ) {
      aggregate {
        sum {
          secondary_volume_24hrs: price
        }
        number_of_secondary_sales_24hrs: count
      }
    }
  }
`

export const USER_STATS_QUERY = gql`
  query UserStats($user: String!) {
    tokenAccountAggregate(
      where: {
        mintObject: { editionMetadata: { tokenOwner: { _eq: $user } } }
        amount: { _gt: "0" }
        owner: { _neq: $user }
      }
      distinctOn: owner
    ) {
      aggregate {
        count
      }
    }
    editionMetadataAggregate(
      where: {
        mintObject: { tokenAccounts: { owner: { _eq: $user }, amount: { _gt: "0" } } }
        tokenOwner: { _neq: $user }
      }
      distinctOn: tokenOwner
    ) {
      aggregate {
        count
      }
    }
  }
`

export const DROP_TIME_QUERY = gql`
  query DropTime($mint: String!) {
    mint(where: { id: { _eq: $mint } }) {
      dropTime {
        dropTime
      }
    }
  }
`

export const VIDEO_LEADERBOARD_QUERY = gql`
  query VideoLeaderboard($offset: Int, $limit: Int, $orderBy: [VideoLeaderboardOrderBy!]) {
    videoLeaderboard(offset: $offset, limit: $limit, orderBy: $orderBy) {
      allPrim
      allSec
      allTime
      mint
      oneDay
      oneDayPrim
      oneHour
      oneDaySec
      oneHourPrim
      oneHourSec
      sevenDaySec
      sixHourPrim
      sevenDays
      sevenDayPrim
      sixHourSec
      sixHours
      thirtyDayPrim
      thirtyDaySec
      thirtyDays
      floorPriceObject {
        floor
      }
      mintObject {
        dropTime {
          dropTime
        }
        milestones {
          endTime
          nextMilestone
        }
        supply
        id
        floorPriceObject {
          floor
        }
        editionMetadata {
          maxSupply
          primarySalePrice
          metadataObject {
            purchaseReceiptsAggregate {
              aggregate {
                sum {
                  price
                }
              }
            }
          }
        }
      }
    }
  }
`

export const CREATOR_LEADERBOARD_QUERY = gql`
  query CreatorLeaderboard($offset: Int, $limit: Int, $orderBy: [CreatorLeaderboardOrderBy!]) {
    creatorLeaderboard(offset: $offset, limit: $limit, orderBy: $orderBy) {
      allCollectors
      allPrimarySales
      allSales
      allSecondarySales
      dayCollectors
      weekSecondarySales
      weekSales
      weekPrimarySales
      weekCollectors
      tokenOwner
      monthSecondarySales
      monthPrimarySales
      monthCollectors
      daySecondarySales
      daySales
      dayPrimarySales
      weekSecondarySalesSol
      weekSalesSol
      weekPrimarySalesSol
      monthSecondarySalesSol
      monthSales
      monthSalesSol
      monthPrimarySalesSol
      daySecondarySalesSol
      daySalesSol
      dayPrimarySalesSol
      allSecondarySalesSol
      allSalesSol
      allPrimarySalesSol
    }
  }
`

export const COLLECTOR_LEADERBOARD_QUERY = gql`
  query CollectorLeaderboard($offset: Int, $limit: Int, $orderBy: [CollectorLeaderboardOrderBy!]) {
    collectorLeaderboard(offset: $offset, limit: $limit, orderBy: $orderBy) {
      allCollected
      allEarned
      allFlipped
      allPercentReturns
      allReturns
      allSpent
      dayCollected
      dayEarned
      dayFlipped
      dayPercentReturns
      dayReturns
      daySpent
      weekSpent
      weekReturns
      weekPercentReturns
      weekFlipped
      weekEarned
      weekCollected
      owner
      monthSpent
      monthReturns
      monthPercentReturns
      monthFlipped
      monthEarned
      monthCollected
    }
  }
`

export const MONETIZATION_RECOMMENDATION_QUERY = gql`
  query MonetizationRecommendationQuery {
    editionMetadataAggregate(
      where: {
        metadataObject: { purchaseReceipts_aggregate: { count: { predicate: { _gt: 0 } } } }
      }
    ) {
      aggregate {
        max {
          maxSupply
          primarySalePrice
        }
        min {
          primarySalePrice
          maxSupply
        }
        avg {
          maxSupply
          primarySalePrice
        }
      }
    }
  }
`

export const DRAFT_DROP_TIME_QUERY = gql`
  query DraftDropTimeQuery($mint: String!) {
    dropTimes(where: { mint: { _eq: $mint } }) {
      dropTime
    }
  }
`

const wsLink = process.browser
  ? new WebSocketLink({
      // if you instantiate in the server, the error will be thrown
      uri:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? 'wss://glass-mainnet.hasura.app/v1/graphql'
          : `wss://possible-crow-14.hasura.app/v1/graphql`,
      options: {
        reconnect: true,
      },
    })
  : null

const httplink = new HttpLink({
  uri:
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? 'https://glass-mainnet.hasura.app/v1/graphql'
      : 'https://possible-crow-14.hasura.app/v1/graphql',
  credentials: 'same-origin',
})

const splitLink = wsLink
  ? split(
      //only create the split in the browser
      // split based on operation type
      ({ query }) => {
        const definition = getMainDefinition(query)
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
      },
      wsLink,
      httplink
    )
  : httplink

// export const client = new ApolloClient({
//   link: new HttpLink({
//     uri: 'https://blue-surf-420079.us-east-1.aws.cloud.dgraph.io/graphql',
//   }),
//   cache: new InMemoryCache(),
// })

export const indexerClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
})
